import { Route } from '@angular/router';
import { coreGuard } from './guards/core.guard';
import { authenticationPageGuard } from './pages/authentication/authentication.guard';
import { bookCompletePageGuard } from './pages/book-complete/book-complete.guard';
import { HomePage } from './pages/home/home.page';
import { guestGuard } from './guards/guest.guard';
import { tipsGuard } from './pages/tips/tips.guard';
import { bookGuard } from './guards/book.guard';
import { googleAuthenticationPageGuard } from './pages/authentication/google-authentication.guard';
import { onboardingGuard } from './pages/onboarding/onboarding.guard';

const readerRoutes: Route = {
  path: 'book/:uuid',
  canActivate: [bookGuard],
  children: [
    {
      path: 'details',
      loadComponent: () => import('./pages/book-details/book-details.page').then(mod => mod.BookDetailsPage),
    },
    {
      path: 'complete',
      loadComponent: () => import('./pages/book-complete/book-complete.page').then(mod => mod.BookCompletePage),
      canActivate: [bookCompletePageGuard],
    },
    {
      path: 'reader',
      children: [
        { path: '', loadComponent: () => import('./pages/book-reader/book-reader.page').then(mod => mod.BookReaderPage) },
        { path: 'font', loadComponent: () => import('./pages/book-reader/book-reader.page').then(mod => mod.BookReaderPage) },
        { path: 'table-of-contents', loadComponent: () => import('./pages/book-reader/book-reader.page').then(mod => mod.BookReaderPage) },
        { path: 'settings', loadComponent: () => import('./pages/book-reader/book-reader.page').then(mod => mod.BookReaderPage) },
      ],
    },
    { path: '**', redirectTo: '/home', pathMatch: 'full' },
  ],
};

const legacyRoutes: Route[] = [
  { path: ':lang/Book/Details/:uuid', redirectTo: 'book/:uuid/details' },
  { path: ':lang/sem/:uuid/:version', redirectTo: 'book/:uuid/details' },
  { path: 'sem/:uuid/:version', redirectTo: 'book/:uuid/details' },
  { path: 'stw/:uuid/:version', redirectTo: 'book/:uuid/details' },
  { path: 'swa/:uuid/:version', redirectTo: 'book/:uuid/details' },
  { path: 'Book/Details/:uuid', redirectTo: 'book/:uuid/details' },
  { path: 'BookSmart/Library', redirectTo: 'saved' },
  { path: 'BookSmart/Account/Login', redirectTo: 'authentication' },
];

export const routes: Route[] = [
  { path: 'onboarding', loadComponent: () => import('./pages/onboarding/onboarding.page').then(mod => mod.OnboardingPage), canActivate: [onboardingGuard] },
  {
    path: '',
    canActivate: [coreGuard],
    children: [
      readerRoutes,
      ...legacyRoutes,
      { path: 'book-error', loadComponent: () => import('./pages/book-error/book-error.page').then(mod => mod.BookErrorPage) },
      {
        path: 'authentication',
        loadComponent: () => import('./pages/authentication/authentication.page').then(mod => mod.AuthenticationPage),
        canActivate: [authenticationPageGuard],
      },
      {
        path: 'authentication/google', // Used for google oauth2 login on web redirection only
        loadComponent: () => import('./pages/authentication/authentication.page').then(mod => mod.AuthenticationPage),
        canActivate: [googleAuthenticationPageGuard],
      },
      {
        path: 'forgot-password',
        loadComponent: () => import('./pages/authentication/authentication.page').then(mod => mod.AuthenticationPage),
        canActivate: [authenticationPageGuard],
      },
      {
        path: 'confirmation-email',
        loadComponent: () => import('./pages/authentication/authentication.page').then(mod => mod.AuthenticationPage),
        canActivate: [authenticationPageGuard],
      },
      { path: 'shelf/:type', loadComponent: () => import('./pages/shelf/shelf.page').then(mod => mod.ShelfPage) },
      {
        path: 'shelf/category/:categoryId',
        loadComponent: () => import('./pages/shelf/shelf.page').then(mod => mod.ShelfPage),
      },
      {
        path: 'tips',
        loadComponent: () => import('./pages/tips/tips.page').then(mod => mod.TipsPage),
        canActivate: [tipsGuard],
      },
      {
        path: 'tips/category/:categoryId',
        loadComponent: () => import('./pages/tips-category/tips-category.page').then(mod => mod.TipsCategoryPage),
        canActivate: [tipsGuard],
      },
      {
        path: '',
        loadComponent: () => import('./components/tabs/tabs.component').then(mod => mod.TabsComponent),
        children: [
          { path: '', redirectTo: 'home' },
          { path: 'home', component: HomePage }, // Lazy load everything except the home page
          { path: 'dashboard', loadComponent: () => import('./pages/dashboard/dashboard.page').then(mod => mod.DashboardPage) },
          { path: 'explore', loadComponent: () => import('./pages/explore/explore.page').then(mod => mod.ExplorePage) },
          { path: 'menu', loadComponent: () => import('./pages/mobile-menu/mobile-menu.page').then(mod => mod.MobileMenuPage) },
          { path: 'saved', loadComponent: () => import('./pages/saved/saved.page').then(mod => mod.SavedPage) },
          { path: 'why-booksmart', loadComponent: () => import('./pages/why-booksmart/why-booksmart.page').then(mod => mod.WhyBooksmartPage) },
          { path: 'settings', loadComponent: () => import('./pages/settings/settings.page').then(mod => mod.SettingsPage) },
          {
            path: 'settings/family',
            loadComponent: () => import('./pages/settings-family/settings-family.page').then(mod => mod.SettingsFamilyPage),
            canActivate: [guestGuard],
          },
          {
            path: 'settings/delete-account',
            loadComponent: () => import('./pages/settings-delete-account/settings-delete-account.page').then(mod => mod.SettingsDeleteAccountPage),
            canActivate: [guestGuard],
          },
          { path: 'how-to-use', loadComponent: () => import('./pages/how-to-use/how-to-use.page').then(mod => mod.HowToUsePage) },
          { path: 'author/:id', loadComponent: () => import('./pages/author/author.page').then(mod => mod.AuthorPage) },
          { path: 'publisher/:id', loadComponent: () => import('./pages/publisher/publisher.page').then(mod => mod.PublisherPage) },
          { path: 'language', loadComponent: () => import('./pages/language/language.page').then(mod => mod.LanguagePage) },
          { path: 'help', loadComponent: () => import('./pages/help/help.page').then(mod => mod.HelpPage) },
          { path: 'library-code', loadComponent: () => import('./pages/library-code/library-code.page').then(mod => mod.LibraryCodePage) },
        ],
      },
      { path: '**', redirectTo: 'home' },
    ],
  },
];
